import { useEffect, useState } from 'react'
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, FormHelperText, Radio, RadioGroup, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router';
import { useAppSelector } from '../../../../../redux/hooks';
import './UserEdit.scss';
import { TokenService } from '../../../../../services/token.service';
import { actionClearEditUserCache, actionDeleteUser, actionGetUsers, actionUpdatePassword, actionUpdateUser } from '../../../../../redux/action/user';
import { useDispatch } from 'react-redux';
import { HttpState } from '../../../../../util/model';
import * as Yup from 'yup';

const UpdateUserPasswordSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, 'Too Short!')
        .max(50, 'Too Long!')
        .required('Required'),
});

export default function UserEdit() {
    const navigate = useNavigate();
    const [deleteUserModal, setDeleteUserModal] = useState(false);
    const userList = useAppSelector(state => state.user.userList);

    const userUpdateState = useAppSelector(state => state.user.userUpdateState);
    const userUpdatePasswordState = useAppSelector(state => state.user.userUpdatePasswordState);
    const userDeleteState = useAppSelector(state => state.user.userDeleteState);
    
    const params = useParams<{ id: string }>();
    const dispatch = useDispatch();
    const user = userList.find(u => u._id === params.id);
    const formik = useFormik({
        initialValues: {
            email: '',
            usertype: 'user',
            enabled: false,
            alertL1: false,
            alertL2: false,
            minPeriod: -1,
            maxPower: -120
        },
        onSubmit: (values) => {
            const body = {
                usertype: values.usertype,
                enabled: values.enabled,
                alertL1: values.alertL1,
                alertL2: values.alertL2,
                minPeriod: +values.minPeriod,
                maxPower: +values.maxPower
            };
            dispatch(actionUpdateUser(user!._id, body) as any);
        }
    })

    useEffect(() => {
        if (user) {
            formik.setValues({
                email: user.email,
                usertype: user.usertype,
                enabled: user.enabled,
                alertL1: user.alertL1,
                alertL2: user.alertL2,
                minPeriod: +user.minPeriod,
                maxPower: +user.maxPower
            });
        }
    }, [user]);
    
    useEffect(() => {
        dispatch(actionClearEditUserCache() as any);
        return () => {dispatch(actionClearEditUserCache() as any)};
    }, []);

    useEffect(() => {
        dispatch(actionGetUsers(false) as any)
    }, [userUpdateState, userUpdatePasswordState]);
    
    useEffect(() => {
      if (userDeleteState === HttpState.Success) {
        dispatch(actionGetUsers(false) as any);
        navigate("/#user"); // Updated for v6
      }
    }, [userDeleteState, dispatch, navigate]);
    
    const passwordResetFormik = useFormik({
        initialValues: {
            password: '',
        },
        onSubmit: (values) => {
            dispatch(actionUpdatePassword(user!._id, values.password) as any);
        },
        validationSchema: UpdateUserPasswordSchema
    })

    if (!user) {
        return (
	    <div className="UserEdit UserEdit-center">
                <CircularProgress />
            </div>
	);
    }
    async function deleteUser() {
        setDeleteUserModal(false);
        dispatch(actionDeleteUser(user!._id) as any);
    }
    
    const mainInfo = TokenService.mainInfo();
    const isMe = mainInfo.id === user._id;
    return (
        <div className="UserEdit">
            <fieldset>
                <legend> UPDATE USER </legend>
                <form onSubmit={formik.handleSubmit}>
                    <TextField
                        name="email"
                        label="Email"
                        variant="outlined"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                        autoComplete="off"
                    />
                    <FormControlLabel
                        control={<Checkbox
                            name="enabled"
                            onChange={formik.handleChange}
                            checked={formik.values.enabled}
                            onBlur={formik.handleBlur}
                        />
                        }
                        disabled={isMe}
                        label="User Enabled"
                    />

                    <RadioGroup
                        defaultValue="user"
                        name="usertype"
                        value={formik.values.usertype}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}

                    >
                        <FormControlLabel disabled={isMe} value="user" control={<Radio />} label="User" />
                        <FormControlLabel disabled={isMe} value="moderator" control={<Radio />} label="Moderator" />
                        <FormControlLabel disabled={isMe} value="admin" control={<Radio />} label="Admin" />
                    </RadioGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="alertL1"
                                onChange={formik.handleChange}
                                checked={formik.values.alertL1}
                                onBlur={formik.handleBlur}
                            />
                        }
                        label="L1 Interference Alert"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="alertL2"
                                onChange={formik.handleChange}
                                checked={formik.values.alertL2}
                                onBlur={formik.handleBlur}
                            />
                        }
                        label="L2 Interference Alert"
                    />
                    <TextField
                        className="User_Add"
                        name="minPeriod"
                        label="Min Period (sec) to Alert"
                        variant="outlined"
                        autoComplete="off"
                        value={formik.values.minPeriod}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <TextField
                        className="User_Add"
                        name="maxPower"
                        label="Max Power (dBm) to Alert"
                        variant="outlined"
                        autoComplete="off"
                        value={formik.values.maxPower}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={
                            isMe ||
                            !formik.isValid ||
                            userUpdateState === HttpState.Progress
                        }
                    >Update User Info</Button>
                    {userUpdateState === HttpState.Failure &&
                        <FormHelperText error>User update error</FormHelperText>
                    }
                    {isMe &&
                        <FormHelperText error>You cannot edit yourself</FormHelperText>
                    }
                </form>
            </fieldset>
            <br />
            <br />
            <fieldset>
                <legend> RESET PASSWORD </legend>
                <form onSubmit={passwordResetFormik.handleSubmit}>

                    <TextField
                        name="password"
                        label="Password"
                        type="password"
                        variant="outlined"
                        autoComplete="off"
                        value={passwordResetFormik.values.password}
                        onChange={passwordResetFormik.handleChange}
                        onBlur={passwordResetFormik.handleBlur}
                    />
                    {passwordResetFormik.errors.password &&
                        <FormHelperText error>{passwordResetFormik.errors.password}</FormHelperText>
                    }
                    <br />

                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={
                            !passwordResetFormik.isValid ||
                            userUpdatePasswordState === HttpState.Progress
                        }
                    >Update Password</Button>
                    {userUpdatePasswordState === HttpState.Failure &&
                        <FormHelperText error>Password update error</FormHelperText>
                    }
                </form>
            </fieldset>
            <br />
            <br />
            <fieldset>
                <legend> DELETE USER </legend>

                <form>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => setDeleteUserModal(true)}
                        disabled={
                            isMe ||
                            !formik.isValid ||
                            userDeleteState === HttpState.Progress
                        }
                    >Delete User</Button>
                    {userDeleteState === HttpState.Failure &&
                        <FormHelperText error>User Delete error</FormHelperText>
                    }
                    {isMe &&
                        <FormHelperText error>You cannot delete yourself</FormHelperText>
                    }
                </form>
            </fieldset>

            <Dialog
                open={deleteUserModal}
                onClose={() => setDeleteUserModal(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Drop/Delete User ?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                    User will be deleted ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteUserModal(false)} color="primary">
                        Disagree
                    </Button>
                    <Button onClick={deleteUser} color="secondary" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}
